import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Alert,
  Toast,
  ToastContainer,
  Spinner,
} from 'react-bootstrap';
import {
  Gear,
  InfoCircle,
  Save,
  Shield,
  Bell,
  Globe,
  Lock,
  Database,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

const SystemSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    siteName: 'CNAMGS Digital',
    siteEmail: 'contact@cnamgs.ga',
    phone: '+241 01 23 45 67',
    currency: 'FCFA',
    notificationEmail: 'alerts@cnamgs.ga',
    maintenanceMode: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    enable2FA: true,
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = () => {
    setLoading(true);
    setTimeout(() => {
      showToast('Paramètres enregistrés avec succès', 'success');
      setLoading(false);
    }, 800);
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast(prev => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <h4 className="main-title mb-0 d-flex align-items-center gap-2">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2">
              <Gear size={24} className="text-primary" />
            </div>
            Paramètres système
          </h4>
          <p className="text-muted mb-0 mt-1">
            Configurez les paramètres généraux de la plateforme CNAMGS
          </p>
        </div>

        <Row className="g-4">
          {/* Informations générales */}
          <Col md={6}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <Globe size={18} className="text-primary" />
                  Informations générales
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom du site</Form.Label>
                    <Form.Control
                      type="text"
                      name="siteName"
                      value={settings.siteName}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email de contact</Form.Label>
                    <Form.Control
                      type="email"
                      name="siteEmail"
                      value={settings.siteEmail}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Téléphone</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone"
                      value={settings.phone}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Devise</Form.Label>
                    <Form.Control
                      type="text"
                      name="currency"
                      value={settings.currency}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Notifications */}
          <Col md={6}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <Bell size={18} className="text-primary" />
                  Notifications
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Email d'alertes</Form.Label>
                    <Form.Control
                      type="email"
                      name="notificationEmail"
                      value={settings.notificationEmail}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Mode maintenance"
                      name="maintenanceMode"
                      checked={settings.maintenanceMode}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Activez pour mettre la plateforme en maintenance.
                    </Form.Text>
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Sécurité */}
          <Col md={6}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <Lock size={18} className="text-primary" />
                  Sécurité
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Délai d'inactivité (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      name="sessionTimeout"
                      value={settings.sessionTimeout}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Tentatives de connexion max</Form.Label>
                    <Form.Control
                      type="number"
                      name="maxLoginAttempts"
                      value={settings.maxLoginAttempts}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Activer l'authentification à deux facteurs (2FA)"
                      name="enable2FA"
                      checked={settings.enable2FA}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Base de données */}
          <Col md={6}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <Database size={18} className="text-primary" />
                  Base de données
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="mb-3">
                  <Button variant="outline-primary" size="sm" className="rounded-pill me-2">
                    Sauvegarder maintenant
                  </Button>
                  <Button variant="outline-secondary" size="sm" className="rounded-pill">
                    Restaurer
                  </Button>
                </div>
                <Alert variant="info" className="small">
                  <InfoCircle size={14} className="me-1" />
                  Dernière sauvegarde : 28/03/2025 02:00
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Bouton d'enregistrement */}
        <div className="mt-4 d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={loading}
            className="rounded-pill px-4"
          >
            {loading ? <Spinner size="sm" className="me-1" /> : <Save size={16} className="me-1" />}
            {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </div>

        <style>{`
          .card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.15) !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default SystemSettings;